import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { DeleteListsForm } from '@vlabs/monokit/entities/lists';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';

import { useDeleteLists } from './useDeleteLists';

interface DeleteListsIconButtonProps {
  listIds: string[];
  onDeleteLists?: VoidFunction;
  enabled?: boolean;
  'data-testid'?: string;
  disabled?: boolean;
  shape?: string;
  size?: string;
  variant?: string;
  children?: React.ReactNode;
}

const displayName = 'DeleteListsIconButton';

function DeleteListsIconButton({
  listIds,
  onDeleteLists = undefined,
  children = undefined,
  enabled = true,
  'data-testid': dataTestId = undefined,
  disabled = false,
  ...props
}: DeleteListsIconButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const modal = useModal();
  const deleteLists = useDeleteLists();

  const isDisabled = !enabled || disabled;

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteListsForm
          listIds={listIds}
          onCancel={modal.close}
          onSubmit={async (values) => {
            await deleteLists.mutateAsync(values);
            onDeleteLists?.();
            modal.close();
          }}
        />,
      )}

      <IconButton
        data-testid={dataTestId}
        disabled={isDisabled}
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        variant="flat"
        {...props}
      >
        {children !== undefined ? children : t('features:delete_lists.button')}
      </IconButton>
    </React.Fragment>
  );
}

DeleteListsIconButton.displayName = displayName;

export { DeleteListsIconButton };
